/* eslint-disable no-magic-numbers */
import { useEffect, useRef } from "react";

import "./style.less";

// halloweensky rezim - nahore cara se zavesenymi netopyry a dynemi, netopyri postupne vzlétaji dolu po strance
// levym klikem jde trefit netopyra (visiciho i leticiho) nebo ducha - omraceny bledne s hvezdickami okolo hlavy a zmizi
// trefena dyne se utrhne a se zrychlenim spadne mimo obrazovku, pri obnoveni netopyru se zavesi zpet

interface IHangingItem {
	type: "bat" | "pumpkin";
	// pozice na care jako podil sirky, at sedi i po zmene velikosti okna
	xRatio: number;
	size: number;
	// delka provazku (dyne)
	stringLength: number;
	// faze houpani
	phase: number;
}

interface IFlyingBat {
	x: number;
	y: number;
	size: number;
	// cas od vzletu v s
	time: number;
	speed: number;
	driftAmp: number;
	driftFreq: number;
	driftPhase: number;
	flapFreq: number;
	// x v okamziku vzletu, k nemu se pricita drift
	startX: number;
	// posledni vykresleny stav - pro omraceni
	rotation: number;
	flap: number;
	open: number;
}

// klasicky (prosteradlo s ocima), vlajici (s rukavy a ocasem)
type TGhostType = "classic" | "flowing";

interface IGhost {
	type: TGhostType;
	x: number;
	// vyska letu, okolo ni se duch vlni
	baseY: number;
	y: number;
	size: number;
	// 1 zleva doprava, -1 zprava doleva
	direction: number;
	speed: number;
	bobAmp: number;
	bobFreq: number;
	time: number;
}

// zasazeny objekt - po omraceni bledne a pada/klesa, hvezdicky krouzi okolo hlavy
interface IStunned {
	x: number;
	y: number;
	rotation: number;
	// stred obezne drahy hvezdicek vuci x,y
	starsX: number;
	starsY: number;
	starsRadius: number;
	// rychlost padu, zrychleni (visici netopyr pada rychleji)
	speedY: number;
	gravity: number;
	maxSpeedY: number;
	time: number;
	draw: (ctx: CanvasRenderingContext2D, time: number) => void;
}

// utrzena dyne i s provazkem
interface IFallingPumpkin {
	item: IHangingItem;
	x: number;
	// misto uvazani provazku
	y: number;
	speedY: number;
	rotation: number;
	// otaceni pri padu (rad/s)
	spin: number;
}

// kratky rozbihajici se kruh v miste zasahu
interface IHitRing {
	x: number;
	y: number;
	time: number;
}

const LINE_Y = 8;
const LINE_WIDTH = 3;
const ITEM_SPACING = 64;
// kazda N-ta polozka je dyne
const PUMPKIN_EVERY = 4;
// cas do prvniho vzletu a mezi vzlety (s)
const FIRST_TAKEOFF = [2, 5];
const TAKEOFF_INTERVAL = [2, 7];
// pauza po odletu vsech netopyru, nez se zase zavesi (s)
const RESTART_DELAY = 3;
// trvani prevraceni z visu do letu (s)
const TAKEOFF_DURATION = 0.6;
// cas do prvniho ducha a mezi duchy (s) - pocita se az od zmizeni predchoziho
const FIRST_GHOST = [5, 12];
const GHOST_INTERVAL = [8, 25];
const BAT_COLOR = "#111";
const GHOST_FACE_COLOR = "#2a1840";
const GHOST_SHADE_COLOR = "#d6cfee";
const EYE_COLOR = "#ff8a00";
// rezerva okolo objektu pro zasah (px) - netopyri jsou mali a pohybuji se
const HIT_MARGIN = 8;
// delka omraceni (s), od kdy zacne blednout
const STUN_DURATION = 1.8;
const STUN_FADE_START = 0.5;
const STARS_COUNT = 5;
// rychlost obehu hvezdicek (rad/s)
const STARS_SPEED = 4;
const STAR_COLOR = "#ffd23f";
const STAR_STROKE_COLOR = "#a87400";
const RING_DURATION = 0.35;
// zrychleni padu utrzene dyne (px/s^2)
const PUMPKIN_GRAVITY = 1200;

function random(min: number, max: number) {
	return min + (Math.random() * (max - min));
}

function createHangingItems(width: number): Array<IHangingItem> {
	const count = Math.max(4, Math.floor(width / ITEM_SPACING));
	const offset = (PUMPKIN_EVERY / 2) >>> 0;

	return Array.from({ length: count }).map((item, ind) => {
		const isPumpkin = (ind + offset) % PUMPKIN_EVERY === 0;

		return {
			type: isPumpkin ? "pumpkin" : "bat",
			xRatio: (ind + 0.5 + random(-0.15, 0.15)) / count,
			size: isPumpkin
				? random(18, 24)
				: random(16, 22),
			stringLength: random(10, 36),
			phase: random(0, Math.PI * 2),
		};
	});
}

/**
 * Netopyr s roztazenymi kridly, stred v 0,0 - flap -1 (kridla dole) az 1 (nahore), open 0-1 rozevreni kridel.
 */
function drawFlyingBat(ctx: CanvasRenderingContext2D, size: number, flap: number, open: number) {
	const tipY = -size * 0.45 * flap;
	const span = size * (0.35 + (0.75 * open));

	ctx.fillStyle = BAT_COLOR;

	for (const side of [-1, 1]) {
		const tipX = side * span;

		ctx.beginPath();
		ctx.moveTo(side * size * 0.1, -size * 0.12);
		// horni hrana kridla
		ctx.quadraticCurveTo(side * span * 0.5, (tipY * 0.6) - (size * 0.35), tipX, tipY - (size * 0.08));
		// spodni hrana - tri oblouky
		const points = [0.7, 0.4, 0.1];
		let prevX = tipX;
		let prevY = tipY;

		for (const ratio of points) {
			const pointX = side * span * ratio;
			const pointY = (tipY * ratio) + (size * 0.18);

			ctx.quadraticCurveTo((prevX + pointX) / 2, ((prevY + pointY) / 2) - (size * 0.16), pointX, pointY);
			prevX = pointX;
			prevY = pointY;
		}

		ctx.closePath();
		ctx.fill();
	}

	// telo
	ctx.beginPath();
	ctx.ellipse(0, size * 0.05, size * 0.15, size * 0.3, 0, 0, Math.PI * 2);
	ctx.fill();

	// hlava s usima
	const headY = -size * 0.28;

	ctx.beginPath();
	ctx.arc(0, headY, size * 0.14, 0, Math.PI * 2);
	ctx.moveTo(-size * 0.12, headY);
	ctx.lineTo(-size * 0.1, headY - (size * 0.26));
	ctx.lineTo(-size * 0.02, headY - (size * 0.1));
	ctx.lineTo(size * 0.02, headY - (size * 0.1));
	ctx.lineTo(size * 0.1, headY - (size * 0.26));
	ctx.lineTo(size * 0.12, headY);
	ctx.fill();

	// oci
	ctx.fillStyle = EYE_COLOR;
	ctx.beginPath();
	ctx.arc(-size * 0.055, headY, size * 0.03, 0, Math.PI * 2);
	ctx.arc(size * 0.055, headY, size * 0.03, 0, Math.PI * 2);
	ctx.fill();
}

/**
 * Visici netopyr hlavou dolu se slozenymi kridly - 0,0 je misto, kde se drzi cary.
 */
function drawHangingBat(ctx: CanvasRenderingContext2D, size: number) {
	ctx.fillStyle = BAT_COLOR;
	ctx.strokeStyle = BAT_COLOR;
	ctx.lineWidth = Math.max(1, size * 0.06);

	// nozicky
	ctx.beginPath();
	ctx.moveTo(-size * 0.08, 0);
	ctx.lineTo(-size * 0.05, size * 0.14);
	ctx.moveTo(size * 0.08, 0);
	ctx.lineTo(size * 0.05, size * 0.14);
	ctx.stroke();

	// slozena kridla - kapka sirsi nahore
	ctx.beginPath();
	ctx.moveTo(0, size * 0.1);
	ctx.bezierCurveTo(size * 0.42, size * 0.12, size * 0.34, size * 0.8, 0, size * 0.95);
	ctx.bezierCurveTo(-size * 0.34, size * 0.8, -size * 0.42, size * 0.12, 0, size * 0.1);
	ctx.fill();

	// lem kridel
	ctx.strokeStyle = "#333";
	ctx.lineWidth = 1;
	ctx.beginPath();
	ctx.moveTo(0, size * 0.2);
	ctx.quadraticCurveTo(size * 0.12, size * 0.55, 0, size * 0.85);
	ctx.stroke();

	// hlava dole, usi miri dolu
	const headY = size * 1.02;

	ctx.fillStyle = BAT_COLOR;
	ctx.beginPath();
	ctx.arc(0, headY, size * 0.13, 0, Math.PI * 2);
	ctx.moveTo(-size * 0.11, headY);
	ctx.lineTo(-size * 0.09, headY + (size * 0.24));
	ctx.lineTo(-size * 0.02, headY + (size * 0.09));
	ctx.lineTo(size * 0.02, headY + (size * 0.09));
	ctx.lineTo(size * 0.09, headY + (size * 0.24));
	ctx.lineTo(size * 0.11, headY);
	ctx.fill();

	ctx.fillStyle = EYE_COLOR;
	ctx.beginPath();
	ctx.arc(-size * 0.05, headY + (size * 0.01), size * 0.028, 0, Math.PI * 2);
	ctx.arc(size * 0.05, headY + (size * 0.01), size * 0.028, 0, Math.PI * 2);
	ctx.fill();
}

/**
 * Dyne na provazku s vyrezanym obliceje - 0,0 je misto uvazani na care.
 */
function drawPumpkin(ctx: CanvasRenderingContext2D, size: number, stringLength: number, glow: number) {
	// provazek
	ctx.strokeStyle = BAT_COLOR;
	ctx.lineWidth = 1.5;
	ctx.beginPath();
	ctx.moveTo(0, 0);
	ctx.lineTo(0, stringLength);
	ctx.stroke();

	// stopka
	ctx.fillStyle = "#4d6b1f";
	ctx.fillRect(-size * 0.08, stringLength, size * 0.16, size * 0.3);

	const centerY = stringLength + (size * 0.25) + (size * 0.75);
	const radiusY = size * 0.75;

	// boky a stred - tri laloky
	ctx.strokeStyle = "#a34700";
	ctx.lineWidth = 1;

	for (const [offsetX, radiusX, color] of [[-size * 0.42, size * 0.55, "#e06a00"], [size * 0.42, size * 0.55, "#e06a00"], [0, size * 0.6, "#ff8c1a"]] as const) {
		ctx.fillStyle = color;
		ctx.beginPath();
		ctx.ellipse(offsetX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
		ctx.fill();
		ctx.stroke();
	}

	// oblicej - svitici
	ctx.save();
	ctx.fillStyle = `rgba(255, ${200 + (glow * 40)}, 60, 1)`;
	ctx.shadowColor = "#ffcc33";
	ctx.shadowBlur = 4 + (glow * 6);

	// oci
	for (const side of [-1, 1]) {
		ctx.beginPath();
		ctx.moveTo(side * size * 0.15, centerY - (size * 0.08));
		ctx.lineTo(side * size * 0.42, centerY - (size * 0.08));
		ctx.lineTo(side * size * 0.28, centerY - (size * 0.32));
		ctx.closePath();
		ctx.fill();
	}

	// nos
	ctx.beginPath();
	ctx.moveTo(-size * 0.07, centerY + (size * 0.08));
	ctx.lineTo(size * 0.07, centerY + (size * 0.08));
	ctx.lineTo(0, centerY - (size * 0.04));
	ctx.closePath();
	ctx.fill();

	// zubata pusa
	ctx.beginPath();
	ctx.moveTo(-size * 0.5, centerY + (size * 0.18));
	ctx.quadraticCurveTo(0, centerY + (size * 0.62), size * 0.5, centerY + (size * 0.18));
	ctx.lineTo(size * 0.3, centerY + (size * 0.3));
	ctx.lineTo(size * 0.2, centerY + (size * 0.22));
	ctx.lineTo(size * 0.08, centerY + (size * 0.32));
	ctx.lineTo(-size * 0.08, centerY + (size * 0.32));
	ctx.lineTo(-size * 0.2, centerY + (size * 0.22));
	ctx.lineTo(-size * 0.3, centerY + (size * 0.3));
	ctx.closePath();
	ctx.fill();
	ctx.restore();
}

/**
 * Klasicky duch - cely bily, jsou videt jen oci; stred v 0,0, oci se divaji ve smeru letu.
 */
function drawGhost(ctx: CanvasRenderingContext2D, size: number, time: number, direction: number) {
	const halfWidth = size * 0.5;
	const top = -size * 0.15;
	const bottom = size * 0.65;
	const waves = 4;
	const waveWidth = size / waves;

	ctx.save();
	// naklon do smeru letu
	ctx.rotate(direction * 0.1);
	ctx.fillStyle = "#fff";
	ctx.shadowColor = "rgba(255, 255, 255, 0.8)";
	ctx.shadowBlur = size * 0.3;

	ctx.beginPath();
	ctx.moveTo(-halfWidth, bottom);
	ctx.lineTo(-halfWidth, top);
	ctx.arc(0, top, halfWidth, Math.PI, Math.PI * 2);
	ctx.lineTo(halfWidth, bottom);

	// vlnity lem zprava doleva, vlny se pohybuji
	for (let ind = 0; ind < waves; ind++) {
		const fromX = halfWidth - (ind * waveWidth);
		const wave = Math.sin((time * 6) + ind) * size * 0.06;

		ctx.quadraticCurveTo(fromX - (waveWidth * 0.5), bottom - (size * 0.18) + wave, fromX - waveWidth, bottom + (ind % 2 ? 0 : size * 0.04));
	}

	ctx.closePath();
	ctx.fill();
	ctx.restore();

	// oci
	const eyesX = direction * size * 0.08;
	const eyesY = -size * 0.2;

	ctx.save();
	ctx.rotate(direction * 0.1);
	ctx.fillStyle = BAT_COLOR;
	ctx.beginPath();
	ctx.ellipse(eyesX - (size * 0.14), eyesY, size * 0.07, size * 0.11, 0, 0, Math.PI * 2);
	ctx.ellipse(eyesX + (size * 0.14), eyesY, size * 0.07, size * 0.11, 0, 0, Math.PI * 2);
	ctx.fill();
	ctx.restore();
}

/**
 * Vlajici duch - protahle telo s ocasem vlajicim za sebou, zvednute rukavy, sikme oci a pusa "o".
 * Kresli se s ocasem doprava (let doleva), pro let doprava se zrcadli; stred hlavy je nad 0,0.
 */
function drawFlowingGhost(ctx: CanvasRenderingContext2D, size: number, time: number, direction: number) {
	const armWave = Math.sin(time * 3) * size * 0.04;
	const tailWave = Math.sin(time * 2.5) * size * 0.06;
	const headY = -size * 0.35;
	const headRadius = size * 0.25;

	ctx.save();
	// naklon do smeru letu, ocas vzdy za duchem
	ctx.rotate(direction * 0.12);
	ctx.scale(-direction, 1);
	ctx.shadowColor = "rgba(255, 255, 255, 0.7)";
	ctx.shadowBlur = size * 0.25;

	// rukavy - za telem
	ctx.fillStyle = "#ece8f8";

	for (const side of [-1, 1]) {
		ctx.beginPath();
		// horni hrana k zaoblene spicce, pak dolu a dva obloucky zpet k telu
		ctx.moveTo(side * size * 0.22, -size * 0.26);
		ctx.quadraticCurveTo(side * size * 0.42, -size * 0.44, side * size * 0.56, (-size * 0.38) + armWave);
		ctx.quadraticCurveTo(side * size * 0.68, -size * 0.3, side * size * 0.62, (-size * 0.08) + armWave);
		ctx.quadraticCurveTo(side * size * 0.56, (-size * 0.01) + armWave, side * size * 0.5, (-size * 0.1) + armWave);
		ctx.quadraticCurveTo(side * size * 0.44, armWave, side * size * 0.38, (-size * 0.1) + armWave);
		ctx.quadraticCurveTo(side * size * 0.3, -size * 0.03, side * size * 0.24, -size * 0.08);
		ctx.closePath();
		ctx.fill();
	}

	// telo s ocasem, svetlejsi vlevo, do fialkova vpravo
	const gradient = ctx.createLinearGradient(-size * 0.3, 0, size * 0.75, 0);

	gradient.addColorStop(0, "#fff");
	gradient.addColorStop(0.45, "#f7f5fc");
	gradient.addColorStop(1, GHOST_SHADE_COLOR);
	ctx.fillStyle = gradient;
	ctx.beginPath();
	ctx.moveTo(-headRadius, headY);
	ctx.arc(0, headY, headRadius, Math.PI, Math.PI * 2);
	ctx.bezierCurveTo(size * 0.27, 0, size * 0.42, size * 0.3, size * 0.78, (size * 0.42) + tailWave);
	ctx.bezierCurveTo(size * 0.6, size * 0.6, size * 0.3, (size * 0.7) + tailWave, size * 0.08, size * 0.64);
	ctx.quadraticCurveTo(-size * 0.02, size * 0.58, -size * 0.1, size * 0.6);
	ctx.bezierCurveTo(-size * 0.28, size * 0.4, -size * 0.27, 0, -headRadius, headY);
	ctx.fill();
	ctx.shadowBlur = 0;

	// zahyby latky smerem k ocasu
	ctx.strokeStyle = GHOST_SHADE_COLOR;
	ctx.lineWidth = size * 0.03;
	ctx.lineCap = "round";
	ctx.beginPath();
	ctx.moveTo(size * 0.02, -size * 0.05);
	ctx.bezierCurveTo(size * 0.06, size * 0.25, size * 0.2, size * 0.45, size * 0.42, (size * 0.52) + tailWave);
	ctx.moveTo(-size * 0.12, size * 0.05);
	ctx.bezierCurveTo(-size * 0.1, size * 0.3, -size * 0.02, size * 0.45, size * 0.12, size * 0.56);
	ctx.stroke();
	ctx.restore();

	// oblicej se nezrcadli, jen se natoci s telem
	ctx.save();
	ctx.rotate(direction * 0.12);
	ctx.fillStyle = GHOST_FACE_COLOR;
	ctx.beginPath();
	// oci sklonene k sobe - ustarany vyraz
	ctx.ellipse(-size * 0.085, headY - (size * 0.02), size * 0.05, size * 0.068, 0.45, 0, Math.PI * 2);
	ctx.fill();
	ctx.beginPath();
	ctx.ellipse(size * 0.085, headY - (size * 0.02), size * 0.05, size * 0.068, -0.45, 0, Math.PI * 2);
	ctx.fill();
	ctx.beginPath();
	ctx.ellipse(0, headY + (size * 0.16), size * 0.05, size * 0.08, 0, 0, Math.PI * 2);
	ctx.fill();
	ctx.restore();
}

/**
 * Peticipa hvezdicka se stredem v centerX, centerY.
 */
function drawStar(ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, rotation: number) {
	ctx.beginPath();

	for (let ind = 0; ind < 10; ind++) {
		const angle = rotation + (ind * Math.PI / 5) - (Math.PI / 2);
		const pointRadius = ind % 2 ? radius * 0.45 : radius;

		ctx.lineTo(centerX + (Math.cos(angle) * pointRadius), centerY + (Math.sin(angle) * pointRadius));
	}

	ctx.closePath();
	ctx.fill();
	ctx.stroke();
}

/**
 * Hvezdicky obihajici po elipse okolo bodu - front false = zadni pulka (za hlavou), true = predni.
 */
function drawDizzyStars(ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number, time: number, front: boolean) {
	const radiusY = radius * 0.35;

	ctx.save();
	ctx.fillStyle = STAR_COLOR;
	ctx.strokeStyle = STAR_STROKE_COLOR;
	ctx.lineWidth = 1;

	// obezna draha - jen zadni a predni pulka, at je hlava mezi nimi
	ctx.save();
	ctx.globalAlpha *= 0.35;
	ctx.strokeStyle = STAR_COLOR;
	ctx.lineWidth = 1.5;
	ctx.beginPath();
	ctx.ellipse(centerX, centerY, radius, radiusY, 0, front ? 0 : Math.PI, front ? Math.PI : Math.PI * 2);
	ctx.stroke();
	ctx.restore();

	for (let ind = 0; ind < STARS_COUNT; ind++) {
		const angle = (time * STARS_SPEED) + (ind * Math.PI * 2 / STARS_COUNT);
		// sin > 0 -> dole na elipse = pred hlavou
		const depth = Math.sin(angle);

		if ((depth >= 0) !== front) {
			continue;
		}

		const depthRatio = (depth + 1) / 2;

		ctx.save();
		ctx.globalAlpha *= 0.55 + (0.45 * depthRatio);
		drawStar(ctx, centerX + (Math.cos(angle) * radius), centerY + (depth * radiusY), Math.max(3, radius * 0.22) * (0.7 + (0.3 * depthRatio)), time * 3);
		ctx.restore();
	}

	ctx.restore();
}

/**
 * Bod otoceny o uhel okolo 0,0.
 */
function rotatePoint(pointX: number, pointY: number, angle: number) {
	return {
		x: (pointX * Math.cos(angle)) - (pointY * Math.sin(angle)),
		y: (pointX * Math.sin(angle)) + (pointY * Math.cos(angle)),
	};
}

export default function Halloween() {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext("2d");

		if (!canvas || !ctx) {
			return () => {};
		}

		const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		let width = 0;
		let height = 0;
		const allItems = createHangingItems(window.innerWidth);
		let hanging = allItems.slice();
		const flying: Array<IFlyingBat> = [];
		let nextTakeoff = random(FIRST_TAKEOFF[0], FIRST_TAKEOFF[1]);
		let restartIn = 0;
		let ghost: IGhost | null = null;
		let stunned: Array<IStunned> = [];
		let rings: Array<IHitRing> = [];
		let fallingPumpkins: Array<IFallingPumpkin> = [];
		let nextGhost = random(FIRST_GHOST[0], FIRST_GHOST[1]);
		// typy duchu se stridaji, prvni je nahodny
		let nextGhostType: TGhostType = Math.random() < 0.5 ? "classic" : "flowing";
		let elapsed = 0;
		let lastTime = performance.now();
		let frameId = 0;

		function resize() {
			const dpr = window.devicePixelRatio || 1;

			width = window.innerWidth;
			height = window.innerHeight;
			canvas.width = Math.round(width * dpr);
			canvas.height = Math.round(height * dpr);
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		}

		function takeoff() {
			const bats = hanging.filter(item => item.type === "bat");

			if (bats.length === 0) {
				return;
			}

			const bat = bats[Math.floor(Math.random() * bats.length)];
			const startX = bat.xRatio * width;

			hanging = hanging.filter(item => item !== bat);
			flying.push({
				x: startX,
				y: LINE_Y + (bat.size * 0.5),
				size: bat.size,
				time: 0,
				speed: random(50, 110),
				driftAmp: random(20, 70),
				driftFreq: random(0.4, 1.1),
				driftPhase: random(0, Math.PI * 2),
				flapFreq: random(7, 11),
				startX,
				rotation: Math.PI,
				flap: 0,
				open: 0,
			});
		}

		function drawLine() {
			ctx.strokeStyle = BAT_COLOR;
			ctx.lineWidth = LINE_WIDTH;
			ctx.beginPath();
			ctx.moveTo(0, LINE_Y);
			ctx.lineTo(width, LINE_Y);
			ctx.stroke();
		}

		// lehke houpani okolo mista zaveseni
		function getSwing(item: IHangingItem) {
			return reducedMotion
				? 0
				: Math.sin((elapsed * 1.3) + item.phase) * (item.type === "pumpkin" ? 0.08 : 0.05);
		}

		function getPumpkinGlow(item: IHangingItem) {
			return (Math.sin((elapsed * 2) + item.phase) + 1) / 2;
		}

		function drawHanging() {
			for (const item of hanging) {
				const swing = getSwing(item);

				ctx.save();
				ctx.translate(item.xRatio * width, LINE_Y);
				ctx.rotate(swing);

				if (item.type === "pumpkin") {
					drawPumpkin(ctx, item.size, item.stringLength, getPumpkinGlow(item));
				} else {
					drawHangingBat(ctx, item.size);
				}

				ctx.restore();
			}
		}

		function updateFlying(delta: number) {
			for (let ind = flying.length - 1; ind >= 0; ind--) {
				const bat = flying[ind];

				bat.time += delta;

				// pri vzletu nejdriv spadne a prevrati se, pak leti dolu s kmitanim do stran
				const takeoffRatio = Math.min(1, bat.time / TAKEOFF_DURATION);
				const speed = bat.speed * takeoffRatio;

				bat.y += speed * delta;
				// drift zacina na nule, at netopyr pri vzletu neposkoci do strany
				const drift = Math.sin((bat.time * bat.driftFreq * Math.PI * 2) + bat.driftPhase) - Math.sin(bat.driftPhase);

				bat.x = bat.startX + (drift * bat.driftAmp * takeoffRatio);

				// postupne mizi ve spodni ctvrtine obrazovky
				const fadeStart = height * 0.75;
				const alpha = bat.y > fadeStart
					? Math.max(0, 1 - ((bat.y - fadeStart) / (height * 0.25)))
					: 1;

				if (alpha <= 0) {
					flying.splice(ind, 1);
				} else {
					const driftVelocity = Math.cos((bat.time * bat.driftFreq * Math.PI * 2) + bat.driftPhase) * bat.driftAmp * bat.driftFreq;

					// hlavou dolu -> normalne, v letu naklon podle smeru
					bat.rotation = (Math.PI * (1 - takeoffRatio)) + (Math.atan2(driftVelocity, Math.max(speed, 1)) * 0.3 * takeoffRatio);
					bat.flap = Math.sin(bat.time * bat.flapFreq);
					bat.open = takeoffRatio;
					ctx.save();
					ctx.globalAlpha = alpha;
					ctx.translate(bat.x, bat.y);
					ctx.rotate(bat.rotation);
					drawFlyingBat(ctx, bat.size * 1.2, bat.flap, bat.open);
					ctx.restore();
				}
			}
		}

		function createGhost(type: TGhostType): IGhost {
			const size = type === "flowing"
				? random(55, 75)
				: random(40, 60);
			const direction = Math.random() < 0.5 ? 1 : -1;
			const baseY = random(height * 0.2, height * 0.75);

			return {
				type,
				x: direction === 1
					? -size
					: width + size,
				baseY,
				y: baseY,
				size,
				direction,
				speed: random(70, 140),
				bobAmp: random(10, 30),
				bobFreq: random(0.3, 0.7),
				time: 0,
			};
		}

		// max. jeden duch - prileti v nahodnem case a preleti pres celou sirku
		function updateGhost(delta: number) {
			if (!ghost) {
				nextGhost -= delta;

				if (nextGhost <= 0) {
					ghost = createGhost(nextGhostType);
					nextGhostType = nextGhostType === "classic" ? "flowing" : "classic";
				}

				return;
			}

			ghost.time += delta;
			ghost.x += ghost.direction * ghost.speed * delta;
			ghost.y = ghost.baseY + (Math.sin(ghost.time * ghost.bobFreq * Math.PI * 2) * ghost.bobAmp);

			if (ghost.x < -ghost.size * 2 || ghost.x > width + (ghost.size * 2)) {
				ghost = null;
				nextGhost = random(GHOST_INTERVAL[0], GHOST_INTERVAL[1]);

				return;
			}

			ctx.save();
			ctx.translate(ghost.x, ghost.y);
			if (ghost.type === "flowing") {
				drawFlowingGhost(ctx, ghost.size, ghost.time, ghost.direction);
			} else {
				drawGhost(ctx, ghost.size, ghost.time, ghost.direction);
			}
			ctx.restore();
		}

		function updateFallingPumpkins(delta: number) {
			for (const pumpkin of fallingPumpkins) {
				pumpkin.speedY += PUMPKIN_GRAVITY * delta;
				pumpkin.y += pumpkin.speedY * delta;
				pumpkin.rotation += pumpkin.spin * delta;

				ctx.save();
				ctx.translate(pumpkin.x, pumpkin.y);
				ctx.rotate(pumpkin.rotation);
				drawPumpkin(ctx, pumpkin.item.size, pumpkin.item.stringLength, getPumpkinGlow(pumpkin.item));
				ctx.restore();
			}

			// i horni konec provazku je pod spodnim okrajem
			fallingPumpkins = fallingPumpkins.filter(pumpkin => pumpkin.y < height);
		}

		function tearPumpkin(item: IHangingItem) {
			hanging = hanging.filter(hangingItem => hangingItem !== item);
			fallingPumpkins.push({
				item,
				x: item.xRatio * width,
				y: LINE_Y,
				// cuknuti nahoru pri utrzeni
				speedY: -60,
				rotation: getSwing(item),
				spin: random(-1.5, 1.5),
			});
		}

		function isInBox(pointX: number, pointY: number, left: number, top: number, right: number, bottom: number) {
			return pointX >= left - HIT_MARGIN && pointX <= right + HIT_MARGIN && pointY >= top - HIT_MARGIN && pointY <= bottom + HIT_MARGIN;
		}

		function stunFlyingBat(bat: IFlyingBat) {
			const size = bat.size * 1.2;
			// hvezdicky nad hlavou, i pri otoceni (vzlet)
			const stars = rotatePoint(0, -size * 0.5, bat.rotation);
			const { open } = bat;

			stunned.push({
				x: bat.x,
				y: bat.y,
				rotation: bat.rotation,
				starsX: stars.x,
				starsY: stars.y,
				starsRadius: size * 0.45,
				speedY: 20,
				gravity: 20,
				maxSpeedY: 60,
				time: 0,
				// kridla napul slozena, pomalu se chveji
				draw: (drawCtx, time) => drawFlyingBat(drawCtx, size, Math.sin(time * 2) * 0.3, open * 0.6),
			});
		}

		function stunHangingBat(bat: IHangingItem) {
			const { size } = bat;

			stunned.push({
				x: bat.xRatio * width,
				y: LINE_Y,
				rotation: 0,
				// hlava je dole (visi hlavou dolu)
				starsX: 0,
				starsY: size * 1.08,
				starsRadius: size * 0.45,
				speedY: 0,
				gravity: 350,
				maxSpeedY: 250,
				time: 0,
				draw: drawCtx => drawHangingBat(drawCtx, size),
			});
		}

		function stunGhost(hitGhost: IGhost) {
			const { size, direction, type, time: ghostTime } = hitGhost;

			stunned.push({
				x: hitGhost.x,
				y: hitGhost.y,
				rotation: 0,
				starsX: 0,
				starsY: type === "flowing" ? -size * 0.68 : -size * 0.72,
				starsRadius: size * (type === "flowing" ? 0.35 : 0.4),
				speedY: 10,
				gravity: 10,
				maxSpeedY: 30,
				time: 0,
				// latka se vlni dal
				draw: (drawCtx, time) => (type === "flowing"
					? drawFlowingGhost(drawCtx, size, ghostTime + time, direction)
					: drawGhost(drawCtx, size, ghostTime + time, direction)),
			});
		}

		/**
		 * Zasah pod kurzorem - kontroluje se od objektu nahore (letici netopyri, visici, duch), zasazen je jen jeden.
		 */
		function hitTest(pointX: number, pointY: number) {
			for (let ind = flying.length - 1; ind >= 0; ind--) {
				const bat = flying[ind];
				const size = bat.size * 1.2;
				// kridla az do size * 1.1, ale tenka - staci zhruba stred
				const halfWidth = size * (0.3 + (0.5 * bat.open));
				// pri vzletu je otoceny hlavou dolu
				const upright = bat.rotation < Math.PI / 2;
				const top = upright ? -size * 0.6 : -size * 0.45;
				const bottom = upright ? size * 0.45 : size * 0.6;

				if (isInBox(pointX, pointY, bat.x - halfWidth, bat.y + top, bat.x + halfWidth, bat.y + bottom)) {
					flying.splice(ind, 1);
					stunFlyingBat(bat);

					return true;
				}
			}

			for (const item of hanging) {
				const itemX = item.xRatio * width;

				// dyne od stopky dolu, sirka vcetne bocnich laloku
				if (item.type === "pumpkin" && isInBox(pointX, pointY, itemX - (item.size * 0.95), LINE_Y + item.stringLength,
					itemX + (item.size * 0.95), LINE_Y + item.stringLength + (item.size * 1.75))) {
					tearPumpkin(item);

					return true;
				}

				if (item.type === "bat" && isInBox(pointX, pointY, itemX - (item.size * 0.42), LINE_Y, itemX + (item.size * 0.42), LINE_Y + (item.size * 1.26))) {
					hanging = hanging.filter(hangingItem => hangingItem !== item);
					stunHangingBat(item);

					return true;
				}
			}

			if (ghost) {
				const { size } = ghost;
				// telo bez rukavu a vlajiciho ocasu
				const halfWidth = size * (ghost.type === "flowing" ? 0.4 : 0.55);

				if (isInBox(pointX, pointY, ghost.x - halfWidth, ghost.y - (size * 0.62), ghost.x + halfWidth, ghost.y + (size * 0.68))) {
					stunGhost(ghost);
					ghost = null;
					nextGhost = random(GHOST_INTERVAL[0], GHOST_INTERVAL[1]);

					return true;
				}
			}

			return false;
		}

		// canvas ma pointer-events: none, klik tedy normalne projde i na stranku
		function onPointerDown(event: PointerEvent) {
			if (event.button !== 0) {
				return;
			}

			if (hitTest(event.clientX, event.clientY)) {
				rings.push({
					x: event.clientX,
					y: event.clientY,
					time: 0,
				});
			}
		}

		function updateStunned(delta: number) {
			for (const item of stunned) {
				item.time += delta;
				item.speedY = Math.min(item.maxSpeedY, item.speedY + (item.gravity * delta));
				item.y += item.speedY * delta;

				const alpha = item.time < STUN_FADE_START
					? 1
					: Math.max(0, 1 - ((item.time - STUN_FADE_START) / (STUN_DURATION - STUN_FADE_START)));
				const starsX = item.x + item.starsX;
				const starsY = item.y + item.starsY;

				ctx.save();
				ctx.globalAlpha = alpha;
				drawDizzyStars(ctx, starsX, starsY, item.starsRadius, item.time, false);
				ctx.save();
				ctx.translate(item.x, item.y);
				// omamene pokyvani
				ctx.rotate(item.rotation + (Math.sin(item.time * 6) * 0.08));
				item.draw(ctx, item.time);
				ctx.restore();
				drawDizzyStars(ctx, starsX, starsY, item.starsRadius, item.time, true);
				ctx.restore();
			}

			stunned = stunned.filter(item => item.time < STUN_DURATION);
		}

		function updateRings(delta: number) {
			ctx.save();
			ctx.strokeStyle = STAR_COLOR;
			ctx.lineWidth = 3;

			for (const ring of rings) {
				ring.time += delta;

				const ratio = Math.min(1, ring.time / RING_DURATION);

				ctx.globalAlpha = 1 - ratio;
				ctx.beginPath();
				ctx.arc(ring.x, ring.y, 6 + (ratio * 24), 0, Math.PI * 2);
				ctx.stroke();
			}

			ctx.restore();
			rings = rings.filter(ring => ring.time < RING_DURATION);
		}

		function frame(now: number) {
			// po prepnuti zalozky muze byt delta velka - omezime ji
			const delta = Math.min((now - lastTime) / 1000, 0.1);

			lastTime = now;
			elapsed += delta;

			if (!reducedMotion) {
				nextTakeoff -= delta;

				if (nextTakeoff <= 0) {
					takeoff();
					nextTakeoff = random(TAKEOFF_INTERVAL[0], TAKEOFF_INTERVAL[1]);
				}

				// vsichni netopyri odleteli (nebo byli sestreleni) a zmizeli - po pauze se zase zavesi a animace jede znovu
				if (flying.length === 0 && stunned.length === 0 && !hanging.some(item => item.type === "bat")) {
					restartIn += delta;

					if (restartIn >= RESTART_DELAY) {
						restartIn = 0;
						// zavesi se i utrzene dyne
						hanging = allItems.slice();
						nextTakeoff = random(FIRST_TAKEOFF[0], FIRST_TAKEOFF[1]);
					}
				}
			}

			ctx.clearRect(0, 0, width, height);

			if (!reducedMotion) {
				updateGhost(delta);
			}

			drawLine();
			drawHanging();
			updateFallingPumpkins(delta);
			updateFlying(delta);
			updateStunned(delta);
			updateRings(delta);

			frameId = requestAnimationFrame(frame);
		}

		resize();
		window.addEventListener("resize", resize);
		window.addEventListener("pointerdown", onPointerDown, { passive: true });
		frameId = requestAnimationFrame(frame);

		return () => {
			cancelAnimationFrame(frameId);
			window.removeEventListener("resize", resize);
			window.removeEventListener("pointerdown", onPointerDown);
		};
	}, []);

	return <canvas ref={canvasRef} className="halloween" aria-hidden="true" />;
}
